import type { RawLocation } from "vue-router";
import type { LabelledConfiguration } from "@/components/platform/common/labelling";
import { mergeConfiguration } from "@/components/platform/common/configurationUtils";
import type { DisplayText } from "@/components/platform/common/displayText";
import { keys } from "@/plugins/i18n";
import type { BmIconName } from "@/components/controls/bmIconMap";

export interface ButtonConfiguration extends LabelledConfiguration {
  type: "primary" | "secondary" | "primary-danger" | "ghost" | "ai";
  isVisible?: boolean | (() => boolean);
  isDisabled?: () => boolean;
  icon?: (() => BmIconName) | BmIconName;
}

export interface RoutableButtonConfiguration extends ButtonConfiguration {
  routeTo: (() => RawLocation) | RawLocation;
}

export interface ActionButtonConfiguration extends ButtonConfiguration {
  onClick: (event: PointerEvent) => void | Promise<void>;
}

export interface MoreButtonConfiguration extends ButtonConfiguration {
  buttons: ButtonConfiguration[];
}

export function isRoutableButtonConfiguration(
  btnOptions: ButtonConfiguration
): btnOptions is RoutableButtonConfiguration {
  return (btnOptions as any).routeTo !== undefined;
}

export function isActionButtonConfiguration(
  btnOptions: ButtonConfiguration
): btnOptions is ActionButtonConfiguration {
  return (btnOptions as any).onClick !== undefined;
}

export function isMoreButtonConfiguration(
  btnOptions: ButtonConfiguration
): btnOptions is MoreButtonConfiguration {
  return (btnOptions as any).buttons !== undefined;
}

interface CollapseStrategy {
  type: "sheet" | "action_button";
  collapsedButtonLabel: DisplayText;
}

export interface ButtonBarConfiguration {
  leftButtons: ButtonConfiguration[];
  rightButtons: ButtonConfiguration[];
  collapseStrategy: CollapseStrategy;
}

class ButtonGroupBuilder {
  protected readonly buttons: ButtonConfiguration[] = [];

  addRoutingButton(buttonConfiguration: RoutableButtonConfiguration) {
    this.buttons.push(buttonConfiguration);
    return this;
  }

  addActionButton(buttonConfiguration: ActionButtonConfiguration) {
    this.buttons.push(buttonConfiguration);
    return this;
  }

  build(): ButtonConfiguration[] {
    return this.buttons;
  }
}

class ButtonBarGroupBuilder extends ButtonGroupBuilder {
  addMoreButton(
    buttonConfiguration: Partial<MoreButtonConfiguration>,
    moreButtonBuilder: (moreButtonBuilder: ButtonGroupBuilder) => void
  ) {
    const defaultMoreButtonConfiguration: MoreButtonConfiguration = {
      label: keys.more,
      type: "secondary",
      buttons: [],
    };

    const moreButtonConfiguration = mergeConfiguration(
      defaultMoreButtonConfiguration,
      buttonConfiguration
    );

    const buttonGroupBuilder = new ButtonGroupBuilder();
    moreButtonBuilder(buttonGroupBuilder);
    moreButtonConfiguration.buttons = buttonGroupBuilder.build();
    this.buttons.push(moreButtonConfiguration);

    return this;
  }
}

export class ButtonBarConfigurationBuilder {
  public readonly leftButtons = new ButtonBarGroupBuilder();
  public readonly rightButtons = new ButtonBarGroupBuilder();
  public collapseStrategy: CollapseStrategy = {
    type: "sheet",
    collapsedButtonLabel: keys.more,
  };

  /**
   * Collapse strategy that collapses all buttons into a single "action button" that is expanded on press.
   */
  withActionButtonCollapseStrategy() {
    this.collapseStrategy = {
      type: "action_button",
      collapsedButtonLabel: keys.actions,
    };
    return this;
  }

  public withCollapseStrategy(collapseStrategy: CollapseStrategy) {
    this.collapseStrategy = collapseStrategy;
    return this;
  }

  build(): ButtonBarConfiguration {
    return {
      leftButtons: this.leftButtons.build(),
      rightButtons: this.rightButtons.build(),
      collapseStrategy: this.collapseStrategy,
    };
  }
}
